import React, { useMemo } from "react";
import { Loader } from "../../commons/loader";
import { ddMMMyyyhhmmaaa } from "../../../utils/helper";
import CourierActivityModal from "./courier-activity-modal";
import CourierTable from "./courier-table";

const pick = (task, a, b) => task[a] ?? task[b];

const taskActivityClass = task => {
  const status = String(pick(task, "status", "status") || "").toLowerCase();
  const statusLabel = String(
    pick(task, "statusDisplay", "status_display") || ""
  ).toLowerCase();
  const outcome = String(
    pick(task, "outcomeDisplay", "outcome_display") || ""
  ).toLowerCase();
  if (status === "completed" || statusLabel.includes("completed")) {
    if (!outcome.includes("not visited")) {
      return "completed";
    }
  }
  if (
    outcome.includes("not visited") ||
    pick(task, "exceptionReason", "exception_reason")
  ) {
    return "issue";
  }
  if (
    status === "enroute" ||
    status === "reached" ||
    statusLabel.includes("enroute") ||
    statusLabel.includes("reached")
  ) {
    return "in-progress";
  }
  return "";
};

const taskTimestamp = task =>
  pick(task, "completedAt", "completed_at") ||
  pick(task, "reachedAt", "reached_at") ||
  pick(task, "modified", "modified") ||
  pick(task, "created", "created");

const taskDescription = task => {
  const place = pick(task, "centerName", "center_name") || "Head Office";
  const parts = [
    `${pick(task, "taskTypeDisplay", "task_type_display") || "Task"} — ${place}`,
    pick(task, "statusDisplay", "status_display")
  ];
  const outcome = pick(task, "outcomeDisplay", "outcome_display");
  if (outcome) {
    parts.push(outcome);
  }
  const samples = pick(task, "sampleCount", "sample_count");
  if (samples != null && samples !== "") {
    parts.push(`${samples} sample(s)`);
  }
  const dispatchId = pick(task, "dispatchId", "dispatch_id");
  if (dispatchId) {
    parts.push(`Dispatch ${dispatchId}`);
  }
  return parts.filter(Boolean).join(" · ");
};

const TripProgressModal = ({ trip, tasks = [], loading, onClose }) => {
  const ordered = useMemo(
    () =>
      [...tasks].sort(
        (a, b) =>
          (pick(a, "sequence", "sequence") || 0) -
          (pick(b, "sequence", "sequence") || 0)
      ),
    [tasks]
  );

  return (
    <CourierActivityModal
      title="Trip Progress"
      subtitle={trip?.code || "Trip"}
      onClose={onClose}
      illustrationAlt="Trip progress"
    >
      {loading && <Loader />}
      {!loading && (
        <>
          <ul className="activity-list courier-trip-activity">
            {ordered.map(task => {
              const ts = taskTimestamp(task);
              return (
                <li key={task.id} className={taskActivityClass(task)}>
                  <span>
                    <span>{ts ? ddMMMyyyhhmmaaa(ts) : "Pending"}</span>
                  </span>
                  <span className="text">{taskDescription(task)}</span>
                </li>
              );
            })}
            {!ordered.length && (
              <li>
                <span className="text">No tasks on this trip yet.</span>
              </li>
            )}
          </ul>

          {!!ordered.length && (
            <div className="courier-progress-table">
              <h4 className="courier-progress-table-title">Task summary</h4>
              <CourierTable>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Type</th>
                    <th>Center</th>
                    <th>Status</th>
                    <th>Outcome</th>
                    <th>Samples</th>
                  </tr>
                </thead>
                <tbody>
                  {ordered.map(task => (
                    <tr key={`row-${task.id}`}>
                      <td>{pick(task, "sequence", "sequence")}</td>
                      <td>
                        {pick(task, "taskTypeDisplay", "task_type_display")}
                      </td>
                      <td>
                        {pick(task, "centerName", "center_name") ||
                          "Head Office"}
                      </td>
                      <td>{pick(task, "statusDisplay", "status_display")}</td>
                      <td>
                        {pick(task, "outcomeDisplay", "outcome_display") || "—"}
                      </td>
                      <td>
                        {pick(task, "sampleCount", "sample_count") != null
                          ? pick(task, "sampleCount", "sample_count")
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </CourierTable>
            </div>
          )}
        </>
      )}
    </CourierActivityModal>
  );
};

export default TripProgressModal;
